import { useEffect, useMemo, useState } from 'react'
import { Timeline } from '../ui/Timeline'
import { CategoryRail } from '../ui/CategoryRail'
import { RecordingPill } from '../ui/RecordingPill'
import { BlockEditor } from '../ui/BlockEditor'
import { PeriodNoteBar } from '../ui/PeriodNoteBar'
import { Totals, totalsFor } from '../ui/Totals'
import { PeriodNav } from '../ui/PeriodNav'
import { useKnownTags, useRangeBlocks } from '../ui/useBlockActions'
import { usePaintCategory } from '../ui/usePaintCategory'
import { useWorkspace } from '../data/workspace'
import { activeCategories } from '../data/settings'
import { excludedCategories } from '../data/insights'
import { EMPTY_NOTE, subscribePeriodNote, writePeriodNote, type PeriodNoteValue } from '../data/periodNotes'
import { dayRange, shiftDayKey, todayKey, weekDayKeys, weekRange, weekStartKey } from '../lib/time'
import { formatDayLabel } from '../lib/format'
import type { Block, DayKey } from '../data/types'

export function WeekPage() {
  const { uid, taxonomy, categories, settings, openBlock } = useWorkspace()
  const [anchor, setAnchor] = useState(() => todayKey(settings.dayStartHour))
  const [editing, setEditing] = useState<Block | null>(null)
  const [note, setNote] = useState<PeriodNoteValue>(EMPTY_NOTE)

  const palette = activeCategories(taxonomy)
  const [paintId, setPaintId] = usePaintCategory(palette, openBlock)
  const excluded = useMemo(() => excludedCategories(categories), [categories])
  const monday = weekStartKey(anchor)
  const week = useMemo(() => weekRange(anchor, settings.dayStartHour), [anchor, settings.dayStartHour])

  // Hiding weekends narrows the columns shown; the totals still cover the whole
  // logical week, because time worked on a Saturday did not stop counting.
  const columns: DayKey[] = settings.hideWeekends ? week.days.slice(0, 5) : week.days

  const { blocks, error, create, resize, move, moveBoundary, limitsFor, edit, quickPatch, split, remove } =
    useRangeBlocks(uid, week.start, week.end)
  const knownTags = useKnownTags(uid)

  const previous = useMemo(() => {
    const range = weekRange(shiftDayKey(monday, -7), settings.dayStartHour)
    return range
  }, [monday, settings.dayStartHour])
  const previousBlocks = useRangeBlocks(uid, previous.start, previous.end).blocks
  const previousTracked = totalsFor(
    previousBlocks,
    categories,
    previous.start.getTime(),
    previous.end.getTime(),
    excluded,
  ).tracked

  useEffect(() => subscribePeriodNote(uid, 'week', monday, setNote), [uid, monday])


  const thisWeek = weekStartKey(todayKey(settings.dayStartHour)) === monday
  const lastColumn = columns[columns.length - 1] ?? monday
  const shownRange = {
    from: week.start.getTime(),
    to: dayRange(lastColumn, settings.dayStartHour).end.getTime(),
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-4 pt-3 pb-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <PeriodNav
          label={`${formatDayLabel(monday, 'd MMM')} – ${formatDayLabel(weekDayKeys(monday)[6] as DayKey, 'd MMM')}`}
          onPrevious={() => setAnchor(shiftDayKey(monday, -7))}
          onNext={() => setAnchor(shiftDayKey(monday, 7))}
          onToday={() => setAnchor(todayKey(settings.dayStartHour))}
          isCurrent={thisWeek}
        />
        {openBlock ? (
          <RecordingPill block={openBlock} category={categories.get(openBlock.categoryId)} />
        ) : null}
        <CategoryRail categories={palette} selectedId={paintId} onSelect={setPaintId} />
      </div>

      <PeriodNoteBar
        label="Week"
        value={note}
        onSave={(value) => void writePeriodNote(uid, 'week', monday, value)}
      />

      {error ? <p className="text-[13px] text-danger">{error}</p> : null}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-xl border border-line bg-surface">
        <Timeline
          days={columns}
          blocks={blocks}
          categories={categories}
          palette={palette}
          knownTags={knownTags}
          dayStartHour={settings.dayStartHour}
          workdayStartHour={settings.workdayStartHour}
          workdayEndHour={settings.workdayEndHour}
          paintCategoryId={paintId}
          showDayHeaders
          onCreate={(bounds) => paintId && void create(bounds, paintId)}
          onResize={(block, bounds) => void resize(block, bounds)}
          onMoveBoundary={(previous, next, at) => void moveBoundary(previous, next, at)}
          onMoveBlock={(block, delta) => void move(block, delta)}
          onSelectBlock={setEditing}
          onQuickPatch={(block, patch) => void quickPatch(block, patch)}
          onSplitBlock={(block, at) => void split(block, at)}
          onDeleteBlock={(block) => void remove(block)}
        />
      </div>

      <Totals
        blocks={blocks}
        categories={categories}
        from={shownRange.from}
        to={shownRange.to}
        excluded={excluded}
        comparison={{ tracked: previousTracked, label: 'last week' }}
      />

      {editing ? (
        <BlockEditor
          block={editing}
          categories={palette}
          limits={limitsFor(editing)}
          knownTags={knownTags}
          onSave={(next) => {
            void edit(editing, next)
            setEditing(null)
          }}
          onDelete={() => {
            void remove(editing)
            setEditing(null)
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}
